import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import * as expenseService from '../../services/expenseService';
import { PayeeAccount, Expense, ExpensePayment, ExpenseFilters, PaginatedResponse } from '../../services/expenseService';
import type { RootState } from '../index';

interface PayeeAccountsState {
  payeeAccounts: PayeeAccount[];
  currentPayeeAccount: (PayeeAccount & { recentExpenses: Expense[] }) | null;
  currentExpense: Expense | null;
  expenses: Expense[];
  payments: ExpensePayment[];
  pagination: {
    page: number;
    limit: number;
    totalPages: number;
    totalCount: number;
    hasNext: boolean;
    hasPrev: boolean;
  };
  expensesPagination: {
    page: number;
    limit: number;
    totalPages: number;
    totalCount: number;
    hasNext: boolean;
    hasPrev: boolean;
  };
  totals: {
    totalDue: number;
    totalPaid: number;
    count: number;
  };
  expenseTotals: {
    totalAmount: number;
    totalPaid: number;
    maintenanceTotal: number;
    salaryTotal: number;
    otherTotal: number;
    statsMonth?: number;
    statsYear?: number;
  };
  filters: ExpenseFilters;
  isLoading: boolean;
  isLoadingExpense: boolean;
  isLoadingExpenses: boolean;
  isLoadingPayments: boolean;
  isCreating: boolean;
  isUpdating: boolean;
  error: string | null;
  paymentsPagination: {
    page: number;
    limit: number;
    totalPages: number;
    totalCount: number;
    hasNext: boolean;
    hasPrev: boolean;
  };
  paymentsTotals: {
    totalAmount: number;
    count: number;
    statsMonth?: number;
    statsYear?: number;
  };
}

const initialState: PayeeAccountsState = {
  payeeAccounts: [],
  currentPayeeAccount: null,
  currentExpense: null,
  expenses: [],
  payments: [],
  pagination: {
    page: 1,
    limit: 20,
    totalPages: 0,
    totalCount: 0,
    hasNext: false,
    hasPrev: false,
  },
  expensesPagination: {
    page: 1,
    limit: 20,
    totalPages: 0,
    totalCount: 0,
    hasNext: false,
    hasPrev: false,
  },
  totals: {
    totalDue: 0,
    totalPaid: 0,
    count: 0,
  },
  expenseTotals: {
    totalAmount: 0,
    totalPaid: 0,
    maintenanceTotal: 0,
    salaryTotal: 0,
    otherTotal: 0,
    statsMonth: undefined,
    statsYear: undefined,
  },
  filters: {},
  isLoading: false,
  isLoadingExpense: false,
  isLoadingExpenses: false,
  isLoadingPayments: false,
  isCreating: false,
  isUpdating: false,
  error: null,
  paymentsPagination: {
    page: 1,
    limit: 20,
    totalPages: 0,
    totalCount: 0,
    hasNext: false,
    hasPrev: false,
  },
  paymentsTotals: {
    totalAmount: 0,
    count: 0,
    statsMonth: undefined,
    statsYear: undefined,
  },
};

// Payee Account Thunks
export const fetchPayeeAccounts = createAsyncThunk(
  'payeeAccounts/fetchPayeeAccounts',
  async (filters: { page?: number; limit?: number; search?: string; hasBalance?: string } = {}, { rejectWithValue }) => {
    try {
      const response = await expenseService.getPayeeAccounts({...filters, limit:20});
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch payee accounts');
    }
  }
);

export const fetchPayeeAccount = createAsyncThunk(
  'payeeAccounts/fetchPayeeAccount',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await expenseService.getPayeeAccount(id);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch payee account');
    }
  }
);

export const createPayeeAccount = createAsyncThunk(
  'payeeAccounts/createPayeeAccount',
  async (data: Partial<PayeeAccount>, { rejectWithValue }) => {
    try {
      const response = await expenseService.createPayeeAccount(data);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to create payee account');
    }
  }
);

export const updatePayeeAccount = createAsyncThunk(
  'payeeAccounts/updatePayeeAccount',
  async ({ id, data }: { id: string; data: Partial<PayeeAccount> }, { rejectWithValue }) => {
    try {
      const response = await expenseService.updatePayeeAccount(id, data);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to update payee account');
    }
  }
);

export const deletePayeeAccount = createAsyncThunk(
  'payeeAccounts/deletePayeeAccount',
  async (id: string, { rejectWithValue }) => {
    try {
      await expenseService.deletePayeeAccount(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to delete payee account');
    }
  }
);

// Expense Thunks
export const fetchExpenses = createAsyncThunk(
  'payeeAccounts/fetchExpenses',
  async (filters: ExpenseFilters = {}, { rejectWithValue }) => {
    try {
      const response = await expenseService.getExpenses({...filters, limit:10});
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch expenses');
    }
  }
);

export const fetchExpense = createAsyncThunk(
  'payeeAccounts/fetchExpense',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await expenseService.getExpense(id);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch expense');
    }
  }
);

export const createExpense = createAsyncThunk(
  'payeeAccounts/createExpense',
  async (data: Partial<Expense>, { rejectWithValue }) => {
    try {
      const response = await expenseService.createExpense(data);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to create expense');
    }
  }
);

export const updateExpense = createAsyncThunk(
  'payeeAccounts/updateExpense',
  async ({ id, data }: { id: string; data: Partial<Expense> }, { rejectWithValue }) => {
    try {
      const response = await expenseService.updateExpense(id, data);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to update expense');
    }
  }
);

export const deleteExpense = createAsyncThunk(
  'payeeAccounts/deleteExpense',
  async (id: string, { rejectWithValue }) => {
    try {
      await expenseService.deleteExpense(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to delete expense');
    }
  }
);

export const assignExpensePayee = createAsyncThunk(
  'payeeAccounts/assignExpensePayee',
  async ({ expenseId, payeeAccountId }: { expenseId: string; payeeAccountId: string | null }, { rejectWithValue }) => {
    try {
      const response = await expenseService.assignPayee(expenseId, payeeAccountId);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to assign payee');
    }
  }
);

// Payment Thunks
export const makePayment = createAsyncThunk(
  'payeeAccounts/makePayment',
  async (data: {
    payeeAccountId?: string;
    expenseId?: string;
    amount: number;
    paymentMethod?: string;
    referenceNumber?: string;
    notes?: string;
  }, { rejectWithValue }) => {
    try {
      const response = await expenseService.createPayment(data);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to make payment');
    }
  }
);

export const fetchPayments = createAsyncThunk(
  'payeeAccounts/fetchPayments',
  async (filters: { page?: number; limit?: number; payeeAccountId?: string; startDate?: string; endDate?: string; paymentMethod?: string } = {}, { rejectWithValue }) => {
    try {
      const response = await expenseService.getPayments({...filters, limit: filters?.limit || 10});
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch payments');
    }
  }
);

export const markExpensePaid = createAsyncThunk(
  'payeeAccounts/markExpensePaid',
  async ({ expenseId, amountPaid }: { expenseId: string; amountPaid?: number }, { rejectWithValue }) => {
    try {
      const response = await expenseService.markExpensePaid(expenseId, amountPaid);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to mark expense as paid');
    }
  }
);

export const markExpenseVoid = createAsyncThunk(
  'payeeAccounts/markExpenseVoid',
  async (expenseId: string, { rejectWithValue }) => {
    try {
      const response = await expenseService.markExpenseVoid(expenseId);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to mark expense as void');
    }
  }
);

const payeeAccountsSlice = createSlice({
  name: 'payeeAccounts',
  initialState,
  reducers: {
    setFilters(state, action: PayloadAction<ExpenseFilters>) {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearFilters(state) {
      state.filters = {};
    },
    clearError(state) {
      state.error = null;
    },
    setCurrentPayeeAccount(state, action: PayloadAction<(PayeeAccount & { recentExpenses: Expense[] }) | null>) {
      state.currentPayeeAccount = action.payload;
    },
    setCurrentExpense(state, action: PayloadAction<Expense | null>) {
      state.currentExpense = action.payload;
    },
    clearCurrentExpense(state) {
      state.currentExpense = null;
    },
    resetPayeeAccounts(state) {
      return initialState;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Payee Accounts
      .addCase(fetchPayeeAccounts.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchPayeeAccounts.fulfilled, (state, action) => {
        state.isLoading = false;
        state.payeeAccounts = action.payload.data;
        state.pagination = {
          page: action.payload.pagination.page,
          limit: action.payload.pagination.limit,
          totalPages: action.payload.pagination.totalPages,
          totalCount: action.payload.totalCount || 0,
          hasNext: action.payload.pagination.hasNext,
          hasPrev: action.payload.pagination.hasPrev,
        };
        const totals = action.payload.totals as { totalDue?: number; totalPaid?: number; count?: number } | undefined;
        state.totals = {
          totalDue: totals?.totalDue || 0,
          totalPaid: totals?.totalPaid || 0,
          count: totals?.count || 0,
        };
      })
      .addCase(fetchPayeeAccounts.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Fetch Single Payee Account
      .addCase(fetchPayeeAccount.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchPayeeAccount.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentPayeeAccount = action.payload;
      })
      .addCase(fetchPayeeAccount.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Create Payee Account
      .addCase(createPayeeAccount.pending, (state) => {
        state.isCreating = true;
        state.error = null;
      })
      .addCase(createPayeeAccount.fulfilled, (state, action) => {
        state.isCreating = false;
        state.payeeAccounts.unshift(action.payload);
      })
      .addCase(createPayeeAccount.rejected, (state, action) => {
        state.isCreating = false;
        state.error = action.payload as string;
      })
      // Update Payee Account
      .addCase(updatePayeeAccount.fulfilled, (state, action) => {
        const index = state.payeeAccounts.findIndex(p => p._id === action.payload._id);
        if (index !== -1) {
          state.payeeAccounts[index] = action.payload;
        }
      })
      // Delete Payee Account
      .addCase(deletePayeeAccount.fulfilled, (state, action) => {
        state.payeeAccounts = state.payeeAccounts.filter(p => p._id !== action.payload);
      })
      // Fetch Expenses
      .addCase(fetchExpenses.pending, (state) => {
        state.isLoadingExpenses = true;
        state.error = null;
      })
      .addCase(fetchExpenses.fulfilled, (state, action) => {
        state.isLoadingExpenses = false;
        state.expenses = action.payload.data;
        state.expensesPagination = {
          page: action.payload.pagination.page,
          limit: action.payload.pagination.limit,
          totalPages: action.payload.pagination.totalPages,
          totalCount: action.payload.totalCount || 0,
          hasNext: action.payload.pagination.hasNext,
          hasPrev: action.payload.pagination.hasPrev,
        };
        const totals = action.payload.totals as { totalAmount?: number; totalPaid?: number; maintenanceTotal?: number; salaryTotal?: number; otherTotal?: number; statsMonth?: number; statsYear?: number } | undefined;
        state.expenseTotals = {
          totalAmount: totals?.totalAmount || 0,
          totalPaid: totals?.totalPaid || 0,
          maintenanceTotal: totals?.maintenanceTotal || 0,
          salaryTotal: totals?.salaryTotal || 0,
          otherTotal: totals?.otherTotal || 0,
          statsMonth: totals?.statsMonth,
          statsYear: totals?.statsYear,
        };
      })
      .addCase(fetchExpenses.rejected, (state, action) => {
        state.isLoadingExpenses = false;
        state.error = action.payload as string;
      })
      // Fetch Single Expense
      .addCase(fetchExpense.pending, (state) => {
        state.isLoadingExpense = true;
        state.error = null;
      })
      .addCase(fetchExpense.fulfilled, (state, action) => {
        state.isLoadingExpense = false;
        state.currentExpense = action.payload;
        const index = state.expenses.findIndex(e => e._id === action.payload._id);
        if (index !== -1) {
          state.expenses[index] = action.payload;
        }
      })
      .addCase(fetchExpense.rejected, (state, action) => {
        state.isLoadingExpense = false;
        state.error = action.payload as string;
      })
      // Create Expense
      .addCase(createExpense.pending, (state) => {
        state.isCreating = true;
        state.error = null;
      })
      .addCase(createExpense.fulfilled, (state, action) => {
        state.isCreating = false;
        state.expenses.unshift(action.payload);
      })
      .addCase(createExpense.rejected, (state, action) => {
        state.isCreating = false;
        state.error = action.payload as string;
      })
      // Update Expense
      .addCase(updateExpense.fulfilled, (state, action) => {
        const index = state.expenses.findIndex(e => e._id === action.payload._id);
        if (index !== -1) {
          state.expenses[index] = action.payload;
        }
      })
      // Delete Expense
      .addCase(deleteExpense.fulfilled, (state, action) => {
        state.expenses = state.expenses.filter(e => e._id !== action.payload);
      })
      // Assign Payee
      .addCase(assignExpensePayee.fulfilled, (state, action) => {
        // const index = state.expenses.findIndex(e => e._id === action.payload._id);
        // if (index !== -1) {
        //   state.expenses[index] = action.payload;
        // }
      })
      // Make Payment
      .addCase(makePayment.pending, (state) => {
        state.isUpdating = true;
        state.error = null;
      })
      .addCase(makePayment.fulfilled, (state) => {
        state.isUpdating = false;
        // Will need to refetch payee accounts and expenses to get updated totals
      })
      .addCase(makePayment.rejected, (state, action) => {
        state.isUpdating = false;
        state.error = action.payload as string;
      })
      // Fetch Payments
      .addCase(fetchPayments.pending, (state) => {
        state.isLoadingPayments = true;
        state.error = null;
      })
      .addCase(fetchPayments.fulfilled, (state, action) => {
        state.isLoadingPayments = false;
        state.payments = action.payload.data;
        state.paymentsPagination = {
          page: action.payload.pagination.page,
          limit: action.payload.pagination.limit,
          totalPages: action.payload.pagination.totalPages,
          totalCount: action.payload.totalCount,
          hasNext: action.payload.pagination.hasNext,
          hasPrev: action.payload.pagination.hasPrev,
        };
        const paymentTotals = action.payload.totals as { totalAmount?: number; count?: number; statsMonth?: number; statsYear?: number } | undefined;
        state.paymentsTotals = {
          totalAmount: paymentTotals?.totalAmount || 0,
          count: paymentTotals?.count || 0,
          statsMonth: paymentTotals?.statsMonth,
          statsYear: paymentTotals?.statsYear,
        };
      })
      .addCase(fetchPayments.rejected, (state, action) => {
        state.isLoadingPayments = false;
        state.error = action.payload as string;
      })
      // Mark Expense Paid
      .addCase(markExpensePaid.pending, (state) => {
        state.isUpdating = true;
        state.error = null;
      })
      .addCase(markExpensePaid.fulfilled, (state, action) => {
        state.isUpdating = false;
        const index = state.expenses.findIndex(e => e._id === action.payload._id);
        if (index !== -1) {
          state.expenses[index] = action.payload;
        }
      })
      .addCase(markExpensePaid.rejected, (state, action) => {
        state.isUpdating = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  setFilters,
  clearFilters,
  clearError,
  setCurrentPayeeAccount,
  setCurrentExpense,
  clearCurrentExpense,
  resetPayeeAccounts,
} = payeeAccountsSlice.actions;

export default payeeAccountsSlice.reducer;
